﻿<script>
	import { onMount } from "svelte";

	import FormRow from "../../../components/FormRow.svelte";
	import FormColumn from "../../../components/FormColumn.svelte";
	import Datepicker from "../../../components/Datepicker.svelte";
	import Select from "../../../components/Select.svelte";
	import NumberInput from "../../../components/NumberInput.svelte";
	import CurrencyInput from "../../../components/CurrencyInput.svelte";
	import { stringListToOptionList } from "../../../components/lib/helpers";
	import { holdingTransactionTypeBuyOnly, holdingTransactionTypeList } from "../../../config";
	import { formatDate, unformatDate } from "../../../helpers/dateHelpers";

	import stockPricesDataMap from "../../../config/stockPrices";

	import uuid4 from "uuid4";

	export let id = uuid4().substring(0, 3);
	export let rowIndex = null;
	export let required = null;
	export let disabled = null;
	export let minPrice = null;
	export let maxPrice = null;
	export let transactionDate = null;
	export let transactionType = null;
	export let numberOfShares = null;
	export let pricePerShare = null;
	export let brokerage = null;
	export let isInitialPublicOffer = false;
	export let handleRemoveRow = () => {};

	let showPriceRangeErrorMsg = false;
	let initialMinPrice = minPrice;
	let initialMaxPrice = maxPrice;
	let invalidPriceForDay = false;
	let invalidPriceForPeriod = false;

	const checkPricePerShare = (e) => {
		//  IPO Transactions do not need any restrictions
		if (!isInitialPublicOffer) {
			if (transactionDate) {
				let unformattedDate = unformatDate(transactionDate);
				let universalDate = formatDate(unformattedDate, "yyyyMMdd");
				let theActualDaysTrade = stockPricesDataMap.get(universalDate);

				if (theActualDaysTrade) {
					minPrice = theActualDaysTrade.low;
					maxPrice = theActualDaysTrade.high;
					invalidPriceForDay = true;
					invalidPriceForPeriod = false;
				} else {
					minPrice = initialMinPrice;
					if (universalDate > 20210629) {
						maxPrice = null; // any value for dates after the data
					} else {
						maxPrice = 11.855; // avg price for the period not in the data
					}
					// maxPrice = initialMaxPrice;
					invalidPriceForDay = false;

					if (pricePerShare < minPrice || (maxPrice && pricePerShare > maxPrice)) {
						invalidPriceForPeriod = true;
					}
				}
			} else {
				invalidPriceForDay = false;
				invalidPriceForPeriod = false;
			}
			console.log("transactionDate", transactionDate, minPrice, maxPrice, pricePerShare);
		}
	};

	const priceRangeError = () => {
		showPriceRangeErrorMsg = true;
	};
	const priceRangeValid = () => {
		showPriceRangeErrorMsg = false;
	};

	$: {
		if (transactionDate || pricePerShare) {
			checkPricePerShare();
		}
	}
</script>

<FormRow>
	<FormColumn cols="3">
		{#if isInitialPublicOffer}
			<Datepicker
				{disabled}
				id="transactionDate_{id}"
				htmlLabel="<strong>Transaction Date</<strong>"
				bind:formattedSelected={transactionDate}
				start={new Date(2020, 10, 18)}
				end={new Date(2020, 11, 4)}
				defaultHighlighted={new Date(2020, 10, 18)}
				placeholder="e.g. 18/11/2020"
				openOnClass="calendar-pop"
				required
			/>
		{:else}
			<Datepicker
				{disabled}
				id="transactionDate_{id}"
				htmlLabel="<strong>Transaction Date</<strong>"
				bind:formattedSelected={transactionDate}
				start={new Date(2020, 11, 4)}
				end={new Date()}
				defaultHighlighted={new Date(2020, 11, 4)}
				placeholder="e.g. 04/11/2020"
				openOnClass="calendar-pop"
				required
			/>
		{/if}
	</FormColumn>
	<FormColumn cols="2">
		{#if isInitialPublicOffer}
			<Select
				{disabled}
				id="transactionType_{id}"
				htmlLabel="<strong>Transaction Type</strong>"
				bind:value={transactionType}
				optionList={stringListToOptionList(holdingTransactionTypeBuyOnly)}
				textProperty="name"
				valueProperty="value"
				required
			/>
		{:else}
			<Select
				{disabled}
				id="transactionType_{id}"
				htmlLabel="<strong>Transaction Type</strong>"
				bind:value={transactionType}
				optionList={stringListToOptionList(holdingTransactionTypeList)}
				textProperty="name"
				valueProperty="value"
				required
			/>
		{/if}
	</FormColumn>
	<FormColumn cols="2">
		<NumberInput
			{disabled}
			id="numberOfShares_{id}"
			htmlLabel="<strong>No. of Shares</strong>"
			bind:value={numberOfShares}
			required
		/>
	</FormColumn>
	<FormColumn cols="2">
		<CurrencyInput
			{disabled}
			id="pricePerShare_{id}"
			htmlLabel="<strong>Price per Share</strong>"
			bind:value={pricePerShare}
			on:change={(e) => checkPricePerShare(e)}
			min={minPrice}
			max={maxPrice}
			bouncerShowErrorEvent={priceRangeError}
			bouncerRemovedErrorEvent={priceRangeValid}
			required
			placeholder="0.00"
		/>
	</FormColumn>
	<FormColumn cols="2">
		<CurrencyInput
			{disabled}
			id="brokerage_{id}"
			htmlLabel="<strong>Brokerage</strong>"
			bind:value={brokerage}
			required
			placeholder="0.00"
		/>
	</FormColumn>
	<FormColumn cols="1" class="last">
		<!-- svelte-ignore a11y-label-has-associated-control -->
		<label>&nbsp;</label>
		<button type="button" class="btn btn-outline-secondary btn-sm" on:click={handleRemoveRow}>Remove</button>
	</FormColumn>

	{#if showPriceRangeErrorMsg && invalidPriceForDay}
		<div id="stockPriceError_{id}" class="errorMessage">
			Value applied to Price per Share does not fall within the range that shares in Nuix were traded for this
			date. Please check the price you are inserting is the accurate average price purchased or sold (i.e. per
			share) for the transaction and not the total purchase or sale price of all the shares traded.
		</div>
	{:else if showPriceRangeErrorMsg && invalidPriceForPeriod}
		<div id="stockPriceError_{id}" class="errorMessage">
			Value applied to Price per Share is higher than shares in Nuix were traded during the relevant period.
			Please check the price you are inserting is the accurate average price purchased or sold (i.e. per share)
			for the transaction and not the total purchase or sale price of all the shares traded.
		</div>
	{/if}
</FormRow>

<style>
	.errorMessage {
		color: #c60f13;
	}
</style>
